/**
 * Semantic tokens — rich classification for syntax highlighting.
 *
 * Classifies each token using both raw token type and surrounding
 * context. Distinguishes:
 *
 *   - Reserved keywords:  "keyword"
 *   - Cascade modifiers ("required", "cascade", "append", "override", "drop"): "modifier"
 *   - Stable IDs (#xyz):  "comment" (rendered gray — they're git-shorthash-like metadata, not load-bearing identifiers)
 *   - String / number / bool literals: "string" / "number" / "keyword"
 *   - Step glyphs (`|` fan, `\` snake): "operator" — a nameless caller
 *   - Identifiers — disambiguated by position:
 *       - At the start of a top-level / body declaration: "type" (kind name)
 *       - As the second identifier on a declaration line: "class" (declaration name)
 *       - After `->` in a process step: "method" (interface ref)
 *       - Inside an aspect block before `:`: "property"
 *       - At top of body before `:`: "property" (field name)
 *       - Anywhere else: "variable"
 *
 * Token modifiers:
 *   - "declaration" — set on the declared name token
 *   - "definition"  — set on the kind name + declared name pair (declarations)
 */

import type {
  SemanticTokens,
  SemanticTokensLegend,
} from "vscode-languageserver";
import type { Token, TokenType } from "@archlang/engine";
import type { FileState } from "../workspace/workspace.js";

const TOKEN_TYPES = [
  "keyword",
  "modifier",
  "type",
  "class",
  "interface",
  "namespace",
  "method",
  "property",
  "variable",
  "string",
  "number",
  "comment",
  "operator",
] as const;

const TOKEN_MODIFIERS = [
  "declaration",
  "definition",
  "readonly",
  "deprecated",
] as const;

type SemanticTokenType = (typeof TOKEN_TYPES)[number];

export const SEMANTIC_TOKEN_TYPES = TOKEN_TYPES;
export const SEMANTIC_TOKEN_MODIFIERS = TOKEN_MODIFIERS;
export const SEMANTIC_TOKENS_LEGEND: SemanticTokensLegend = {
  tokenTypes: TOKEN_TYPES as unknown as string[],
  tokenModifiers: TOKEN_MODIFIERS as unknown as string[],
};

const DECLARATION_BIT = 1 << 0;
const DEFINITION_BIT = 1 << 1;

const KEYWORD_TOKENS: ReadonlySet<TokenType> = new Set([
  "TYPE",
  "IN",
  "LABELS",
  "PROCESS",
  "SUBPROCESS",
  "VIEW",
  "IF",
  "ELSE",
  "SELECT",
  "ONE",
  "EACH",
  "TRY",
  "CATCH",
  "PARALLEL",
  "BRANCH",
  "JOIN",
  "RACE",
  "OUT",
  "AWAIT",
  "REVERSIBLE",
  "UNWIND",
  "ON",
  "DO",
  "GO",
  "AS",
  "DIST",
  "FAIL",
  "FINISH",
  "FOCUS",
  "GROUP",
  "BY",
  "LAYOUT",
  "INCLUDE",
  "EXCLUDE",
  // v0.5 packages/spaces/visibility keywords.
  "EXPORT",
  "USE",
  "FROM",
] as readonly TokenType[]);

const MODIFIER_TOKENS: ReadonlySet<TokenType> = new Set([
  "REQUIRED",
  "CASCADE",
  "APPEND",
  "OVERRIDE",
  "DROP",
]);

interface Classification {
  type: SemanticTokenType;
  modifiers: number;
}

export function provideSemanticTokens(
  file: FileState,
  ..._rest: unknown[]
): SemanticTokens {
  const data: number[] = [];
  let prevLine = 0;
  let prevChar = 0;

  // Pre-compute per-token classification using a small context window.
  const classifications = classifyTokens(file.tokens);

  for (let i = 0; i < file.tokens.length; i++) {
    const t = file.tokens[i]!;
    const c = classifications[i];
    if (!c) continue;

    const start = t.span.start;
    const length = t.span.end.offset - t.span.start.offset;
    if (length <= 0) continue;

    const line = start.line - 1;
    const character = start.column - 1;
    const deltaLine = line - prevLine;
    const deltaStart = deltaLine === 0 ? character - prevChar : character;

    const tokenTypeIdx = TOKEN_TYPES.indexOf(c.type);
    if (tokenTypeIdx < 0) continue;

    data.push(deltaLine, deltaStart, length, tokenTypeIdx, c.modifiers);
    prevLine = line;
    prevChar = character;
  }

  return { data };
}

// =============================================================================
// Classification
// =============================================================================

/**
 * Walks the token stream once, deciding for each token what semantic
 * type it should be highlighted as. Uses a small context window for
 * disambiguation but no full AST resolution — good enough for fast
 * incremental highlighting.
 */
function classifyTokens(tokens: readonly Token[]): Array<Classification | null> {
  const result: Array<Classification | null> = new Array(tokens.length).fill(null);
  // Track whether we're inside a `aspect { ... }` block so identifiers
  // before a colon get classified as aspects.
  const aspectBlockStack: number[] = []; // brace-depth at which the current aspect block opened
  let braceDepth = 0;

  // Also track a "decl-line cursor": after we see a kind keyword + name,
  // mark them as type/class.
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;

    switch (t.type) {
      case "EOF":
        result[i] = null;
        continue;
      case "STRING":
        result[i] = { type: "string", modifiers: 0 };
        continue;
      case "NUMBER":
        result[i] = { type: "number", modifiers: 0 };
        continue;
      case "TRUE":
      case "FALSE":
        result[i] = { type: "keyword", modifiers: 0 };
        continue;
      case "STABLE_ID":
        result[i] = { type: "comment", modifiers: 0 };
        continue;
      case "LBRACE":
        braceDepth++;
        if (i >= 1 && tokens[i - 1]?.type === "LABELS") {
          aspectBlockStack.push(braceDepth);
        }
        result[i] = null;
        continue;
      case "RBRACE":
        if (aspectBlockStack.length > 0 && aspectBlockStack[aspectBlockStack.length - 1] === braceDepth) {
          aspectBlockStack.pop();
        }
        braceDepth--;
        result[i] = null;
        continue;
      // The step glyphs (`|` fan, `\` snake, §4.13.4) carry no name, but they are
      // not punctuation either: each one IS the step's caller, resolved from the
      // previous sibling. Classify them so a client paints them as the operators
      // they are rather than leaving them to a regex-level fallback.
      case "PIPE":
      case "BACKSLASH":
        result[i] = { type: "operator", modifiers: 0 };
        continue;
      case "GT":
      case "EQUALS":
      case "DOT":
      case "COLON":
      case "COMMA":
      case "LPAREN":
      case "RPAREN":
      case "LBRACKET":
      case "RBRACKET":
        result[i] = null;
        continue;
      case "ERROR":
        result[i] = null;
        continue;
    }

    if (KEYWORD_TOKENS.has(t.type)) {
      // v0.8.2: a reserved word can be a NAME. If this keyword token sits in a
      // name position, classify it as a name (interface / ref / decl) rather than
      // a construct keyword.
      if (isKeywordUsedAsName(tokens, i)) {
        result[i] = classifyIdentifier(tokens, i, aspectBlockStack.length > 0);
        continue;
      }
      result[i] = { type: "keyword", modifiers: 0 };
      continue;
    }
    if (MODIFIER_TOKENS.has(t.type)) {
      result[i] = { type: "modifier", modifiers: 0 };
      continue;
    }

    // `*` immediately after `cascade` is the cascade-group marker;
    // render as a modifier so editors colour it alongside `cascade`.
    if (t.type === "STAR" && i > 0 && tokens[i - 1]?.type === "CASCADE") {
      result[i] = { type: "modifier", modifiers: 0 };
      continue;
    }

    if (t.type === "IDENTIFIER") {
      result[i] = classifyIdentifier(tokens, i, aspectBlockStack.length > 0);
    }
  }

  return result;
}

/**
 * v0.8.2: is the reserved-word token at `i` used as a NAME (not a construct)?
 * Unambiguous name positions: a callee/member after `.`/`>`, a caller head before
 * the step arrow. A declaration name (`<type> <keyword-name>`) is a name when the
 * preceding type is a lowercase kind (`service`/`rest_read`), which distinguishes
 * it from a PascalCase owner + construct (`Orders select { … }`).
 */
function isKeywordUsedAsName(tokens: readonly Token[], i: number): boolean {
  const prev = prevSignificant(tokens, i - 1);
  if (!prev) return false;
  if (prev.type === "DOT" || prev.type === "GT") return true;
  if (stepArrowFollows(tokens, i)) return true;
  if (prev.type === "IDENTIFIER" && sameLine(prev, tokens[i]!) && /^\p{Ll}/u.test(prev.value)) {
    return true;
  }
  return false;
}

function classifyIdentifier(
  tokens: readonly Token[],
  i: number,
  insideAspectBlock: boolean
): Classification {
  const next = nextSignificant(tokens, i + 1);
  const prev = prevSignificant(tokens, i - 1);

  // Re-entry labels (PROCESS.md §7): the identifier after `as` is the label
  // DEFINITION; after `go` it re-enters that label. Highlight the def as a
  // declared name and the `go` use as a reference (like an interface ref).
  if (prev?.type === "AS") {
    return { type: "class", modifiers: DECLARATION_BIT };
  }
  if (prev?.type === "GO") {
    return { type: "method", modifiers: 0 };
  }

  // Inside an aspect block, identifier before `:` is an aspect key (property).
  if (insideAspectBlock && next?.type === "COLON") {
    return { type: "property", modifiers: 0 };
  }

  // Process-step caller head (§4.13): an identifier whose dotted run
  // `IDENT ('.' IDENT)*` is immediately followed by the call arrow `>` and is
  // not itself preceded by `>`/`.` (which would make it a callee or a path
  // segment) is the step's caller — a module/actor reference. Classify it as a
  // plain reference so a bare step (`A > B`, `> B`, `A > B > C`) is never
  // mis-tokenized as a field assignment or a declaration name.
  if (prev?.type !== "GT" && prev?.type !== "DOT" && stepArrowFollows(tokens, i)) {
    return { type: "variable", modifiers: 0 };
  }

  // Identifier followed by COLON at body level → field name.
  // Walk through dotted prefixes (ext.cmdb.ci, repo.url) — all parts are
  // property; we let the dot punctuation separate them visually.
  if (next?.type === "COLON") {
    return { type: "property", modifiers: 0 };
  }
  // Mid-dotted-path token: `repo.url`'s `repo` has next=DOT, then a deeper
  // ident, then COLON. Classify as property.
  if (next?.type === "DOT") {
    // Look ahead for COLON after the dotted chain.
    let j = i + 1;
    while (tokens[j]?.type === "DOT" && tokens[j + 1]?.type === "IDENTIFIER") j += 2;
    if (tokens[j]?.type === "COLON") {
      return { type: "property", modifiers: 0 };
    }
  }
  // Same: previous token was a DOT and the chain ends in COLON.
  if (prev?.type === "DOT") {
    let j = i + 1;
    while (tokens[j]?.type === "DOT" && tokens[j + 1]?.type === "IDENTIFIER") j += 2;
    if (tokens[j]?.type === "COLON") {
      return { type: "property", modifiers: 0 };
    }
  }

  // After the call arrow `>` → process callee (interface ref).
  if (prev?.type === "GT") {
    return { type: "method", modifiers: 0 };
  }
  // Also any segment of a path after `>`: walk back through dot chain.
  if (prev?.type === "DOT") {
    let j = i - 2;
    while (j >= 0 && tokens[j]?.type === "IDENTIFIER" && tokens[j - 1]?.type === "DOT") j -= 2;
    if (j >= 0 && tokens[j]?.type === "IDENTIFIER" && tokens[j - 1]?.type === "GT") {
      return { type: "method", modifiers: 0 };
    }
  }

  // Declaration head: identifier followed by an IDENTIFIER (kind + name)
  // or by a STABLE_ID + IDENTIFIER (kind + #id + name). A declaration's kind +
  // name always sit on ONE line, so these adjacency checks require the same
  // line — else a token ending a line reads as a kind because the NEXT line
  // happens to start with an identifier (cross-line bleed).
  const self = tokens[i];
  if (next?.type === "IDENTIFIER" && sameLine(self, next)) {
    // This is a kind name (e.g., `microservice` in `microservice Foo`).
    return { type: "type", modifiers: 0 };
  }
  if (next?.type === "STABLE_ID" && sameLine(self, next) && tokens[lookaheadAfter(tokens, i + 1, "STABLE_ID")]?.type === "IDENTIFIER") {
    return { type: "type", modifiers: 0 };
  }
  // Second identifier following a kind: that's the declared name.
  if ((prev?.type === "IDENTIFIER" || prev?.type === "STABLE_ID") && sameLine(prev, self)) {
    const beforeChain = prev?.type === "STABLE_ID" ? prevSignificant(tokens, indexOf(tokens, prev) - 1) : prev;
    if (beforeChain?.type === "IDENTIFIER") {
      return { type: "class", modifiers: DECLARATION_BIT | DEFINITION_BIT };
    }
  }

  // Any other identifier — treat as a reference.
  return { type: "variable", modifiers: 0 };
}

/** True when the identifier at `i` begins a dotted run `IDENT ('.' IDENT)*`
 *  that is immediately followed by the call arrow `>` — i.e. the head of a
 *  process step's caller. */
function stepArrowFollows(tokens: readonly Token[], i: number): boolean {
  if (tokens[i]?.type !== "IDENTIFIER") return false;
  let j = i + 1;
  while (tokens[j]?.type === "DOT" && tokens[j + 1]?.type === "IDENTIFIER") j += 2;
  return tokens[j]?.type === "GT";
}

/** Two tokens are on the same source line (declarations never span lines). */
function sameLine(a: Token | undefined, b: Token | undefined): boolean {
  return !!a && !!b && a.span.start.line === b.span.start.line;
}

function nextSignificant(tokens: readonly Token[], from: number): Token | undefined {
  return tokens[from];
}
function prevSignificant(tokens: readonly Token[], from: number): Token | undefined {
  return tokens[from];
}

function lookaheadAfter(tokens: readonly Token[], idx: number, type: TokenType): number {
  if (tokens[idx]?.type === type) return idx + 1;
  return idx;
}

function indexOf(tokens: readonly Token[], target: Token): number {
  for (let i = 0; i < tokens.length; i++) if (tokens[i] === target) return i;
  return -1;
}

// Backwards compat
export const provideSemanticTokensLegend = (): SemanticTokensLegend =>
  SEMANTIC_TOKENS_LEGEND;
